import styles from './AssistedModeFilterDialog.module.css';

import { Field, FieldArray, Formik, useField } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';

import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import UiBlocker from 'components/bng/ui/UiBlocker';
import BngField from 'components/bng/form/BngField';
import BngForm from 'components/bng/form/BngForm';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngIconButton from 'components/bng/ui/BngIconButton';
import BngButton from 'components/bng/ui/BngButton';
import { BngMaskedField, Mask } from 'components/bng/form/BngMaskedField';
import { bngYup } from 'components/bng/form/yup/BngYup';
import Icon from 'components/ui/common/Icon';

export function checkNullCondition(condition = '') {
  return condition === 'IS NULL' || condition === 'IS NOT NULL';
}

const formValidationSchema = bngYup((yup) => {
  return yup.object().shape({
    filters: yup
      .array(
        yup.object({
          selectedField: yup.string().required().default(''),
          condition: yup.string().required().default(''),
          value: yup
            .string()
            .when('condition', {
              is: (val) => val && !checkNullCondition(val),
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            })
            .default(''),
          operator: yup.string().nullable().default('AND'),
        })
      )
      .default([]),
  });
});

export function OperatorToggle({ name }) {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField(name);

  const handleToggle = (operator) => {
    helpers.setValue(operator);
  };

  return (
    <div className={styles.operatorToggle}>
      <BngButton
        onClick={() => handleToggle('AND')}
        className={`${styles.operatorButton} ${field.value === 'AND' ? styles.activeOperator : ''}`}
      >
        {t('AND')}
      </BngButton>
      <BngButton
        onClick={() => handleToggle('OR')}
        className={`${styles.operatorButton} ${field.value === 'OR' ? styles.activeOperator : ''}`}
      >
        {t('OR')}
      </BngButton>
    </div>
  );
}

export function buildComparisonOptions(t) {
  return {
    numeric: [
      { value: '=', label: `= ${t('ag-grid.equals')}` },
      { value: '!=', label: `≠ ${t('ag-grid.notEqual')}` },
      { value: '>', label: `> ${t('ag-grid.greaterThan')}` },
      { value: '>=', label: `≥ ${t('ag-grid.greaterThanOrEqual')}` },
      { value: '<', label: `< ${t('ag-grid.lessThan')}` },
      { value: '<=', label: `≤ ${t('ag-grid.lessThanOrEqual')}` },
      { value: 'IS NULL', label: t('ag-grid.blank') },
      { value: 'IS NOT NULL', label: t('ag-grid.notBlank') },
    ],
    text: [
      { value: 'LIKE CONTAINS', label: `${t('ag-grid.contains')}` },
      { value: 'NOT LIKE CONTAINS', label: `${t('ag-grid.notContains')}` },
      { value: 'LIKE STARTS WITH', label: `${t('ag-grid.startsWith')}` },
      { value: 'LIKE ENDS WITH', label: `${t('ag-grid.endsWith')}` },
      { value: '=', label: `= ${t('ag-grid.equals')}` },
      { value: '!=', label: `≠ ${t('ag-grid.notEqual')}` },
      { value: 'IS NULL', label: t('is.null') },
      { value: 'IS NOT NULL', label: t('not.null') },
    ],
    bool: [{ value: '=', label: `= ${t('ag-grid.equals')}` }],
  };
}

export default function AssistedModeFilterDialog({ closeModal, nodeId, nodes = [], onChange = _.noop }) {
  const { t } = useTranslation();

  const comparisonOptions = useMemo(() => buildComparisonOptions(t), []);

  const handleSubmit = useCallback(
    (dialogValues) => {
      const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          node = _.cloneDeep(node);
          node.data.tableInfo.filters = dialogValues.filters.map((filter, index) => {
            const selectedField = fields.find((f) => f.name === filter.selectedField);
            const fieldType = selectedField?.type;

            const condition = fieldType === 'BOOL' ? '=' : filter.condition;

            return {
              fieldName: filter.selectedField,
              condition: condition,
              value: filter.value,
              ...(index > 0 ? { operator: filter.operator } : {}),
            };
          });
        }

        return node;
      });
      onChange(updatedNodes);
      closeModal();
    },
    [nodes, nodeId]
  );

  const currentNode = nodes.find((node) => node.id === nodeId);
  const fields = currentNode?.data?.tableInfo?.fields || [];
  const existingFilters = currentNode?.data?.tableInfo?.filters || [];

  const getDefaultCondition = (fieldName) => {
    const field = fields.find((f) => f.name === fieldName);
    return field?.type === 'BOOL' ? '=' : '';
  };

  const initialFilters = existingFilters.map((filter, index) => ({
    selectedField: filter.fieldName || '',
    condition: filter.condition || getDefaultCondition(filter.fieldName),
    value: filter.value || '',
    operator: index === 0 ? null : filter.operator || 'AND',
  }));
  const tableInfo = currentNode.data.tableInfo;
  const tableName = tableInfo.alias ? `${tableInfo.alias} (${tableInfo.table})` : tableInfo.table;

  return (
    <Dialog
      className={`${styles.dialogContainer}`}
      title={`${t('filter')} - ${tableName}`}
      onClose={closeModal}
      newDialogLayout
    >
      <UiBlocker>
        <Formik
          initialValues={{
            filters: initialFilters,
          }}
          onSubmit={handleSubmit}
          validationSchema={formValidationSchema}
        >
          {({ values, submitCount, setFieldValue }) => {
            useEffect(() => {
              values.filters.forEach((filter, index) => {
                const isNullCondition = checkNullCondition(filter.condition);
                if (isNullCondition && filter.value !== '') {
                  setFieldValue(`filters.${index}.value`, '');
                }
              });
            }, [values.filters]);

            return (
              <BngForm>
                <Dialog.Body className={styles.filtersWrapper}>
                  <FieldArray name="filters">
                    {({ push, remove }) => (
                      <div className={styles.filterGroups}>
                        <div className={styles.filterHeader}>
                          <div className={styles.headerTitle}>{t('configure.filters')}</div>
                          <BngIconButton
                            icon={'add_circle'}
                            onClick={() =>
                              push({
                                selectedField: '',
                                condition: '',
                                value: '',
                                operator: values.filters.length > 0 ? 'AND' : null,
                              })
                            }
                            className={styles.addButton}
                          />
                        </div>
                        {values.filters.length === 0 ? (
                          <div className={styles.emptyFiltersMessage}>
                            {t('click.on.icon')}
                            <Icon icon={'add_circle'} className={styles.addButton} />
                            {t('to.add.filter')}
                          </div>
                        ) : (
                          values.filters.map((filter, index) => {
                            const selectedFieldName = values.filters[index].selectedField;
                            const selectedField = fields.find((f) => f.name === selectedFieldName);
                            const fieldType = selectedField?.type;
                            const isValueDisabled = checkNullCondition(filter.condition);

                            return (
                              <div key={index} className={styles.filterGroup}>
                                {index > 0 && <OperatorToggle name={`filters.${index}.operator`} />}
                                <div className={styles.filterFieldsConfigurationWrapper}>
                                  <Field
                                    name={`filters.${index}.selectedField`}
                                    className={`${styles.filterFieldsSelect}`}
                                    component={BngField}
                                    label={t('field')}
                                    required={true}
                                    inputComponent={BngSelectSearch}
                                    options={fields.map((f) => ({
                                      value: f.name,
                                      label: f.name,
                                    }))}
                                    onChange={(value) => {
                                      setFieldValue(`filters.${index}.selectedField`, value);
                                      const selectedField = fields.find((f) => f.name === value);
                                      if (selectedField?.type === 'BOOL') {
                                        setFieldValue(`filters.${index}.condition`, '=');
                                      } else {
                                        setFieldValue(`filters.${index}.condition`, '');
                                      }
                                      setFieldValue(`filters.${index}.value`, '');
                                    }}
                                  />
                                  <Field
                                    name={`filters.${index}.condition`}
                                    className={styles.filterFieldsSelect}
                                    component={BngField}
                                    label={t('condition')}
                                    required={true}
                                    inputComponent={BngSelectSearch}
                                    disabled={fieldType === 'BOOL'}
                                    options={
                                      fieldType === 'NUMERIC' || fieldType === 'DATE'
                                        ? comparisonOptions.numeric
                                        : fieldType === 'TEXT'
                                        ? comparisonOptions.text
                                        : fieldType === 'BOOL'
                                        ? comparisonOptions.bool
                                        : []
                                    }
                                  />
                                  {fieldType === 'BOOL' ? (
                                    <Field
                                      name={`filters.${index}.value`}
                                      className={styles.filterFieldsInput}
                                      component={BngField}
                                      label={t('value')}
                                      required={true}
                                      inputComponent={BngSelectSearch}
                                      options={[
                                        { value: 'TRUE', label: t('assisted.mode.filter.true') },
                                        { value: 'FALSE', label: t('assisted.mode.filter.false') },
                                      ]}
                                    />
                                  ) : fieldType === 'DATE' ? (
                                    <Field
                                      name={`filters.${index}.value`}
                                      className={styles.filterFieldsInput}
                                      component={BngField}
                                      label={t('value')}
                                      disabled={isValueDisabled}
                                      required={!isValueDisabled}
                                      inputComponent={BngMaskedField}
                                      mask={Mask.DATE}
                                      asDate="br"
                                    />
                                  ) : (
                                    <Field
                                      name={`filters.${index}.value`}
                                      className={styles.filterFieldsInput}
                                      component={BngField}
                                      label={t('value')}
                                      disabled={isValueDisabled}
                                      required={!isValueDisabled}
                                      placeholder={!isValueDisabled ? t('write.value.here') : ''}
                                    />
                                  )}
                                  <BngIconButton
                                    icon={'close'}
                                    onClick={() => {
                                      remove(index);
                                    }}
                                    className={styles.removeButton}
                                  />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </FieldArray>
                </Dialog.Body>
                <Dialog.Footer>
                  <DefaultDialogActions
                    closeModal={closeModal}
                    submitCount={submitCount}
                    okLabel="apply"
                    cancelLabel="cancel"
                  />
                </Dialog.Footer>
              </BngForm>
            );
          }}
        </Formik>
      </UiBlocker>
    </Dialog>
  );
}
